import { useEffect, useState } from "react";
import {
  environmentSyncApi,
  SyncOrchestrationInstance,
} from "../api/environmentSyncApi";
import { Problem } from "../api/backendApi";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Button, Form, Spinner, Table } from "react-bootstrap";

function EnvironmentSyncPage() {
  const [syncAllFiles, setSyncAllFiles] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [runningInterval, setRunningInterval] = useState<
    NodeJS.Timer | undefined
  >();
  const [syncInProgress, setSyncInProgress] = useState<
    SyncOrchestrationInstance | undefined
  >(undefined);
  const [syncOverview, setSyncOverview] = useState<SyncOrchestrationInstance[]>(
    [],
  );
  const [latestSuccesfulRun, setLatestSuccesfulRun] = useState<
    SyncOrchestrationInstance | undefined
  >(undefined);

  useEffect(() => {
    getSyncInstances();
  }, []);

  useEffect(() => {
    if (syncInProgress && !runningInterval) {
      const interval = setInterval(getSyncInstances, 30 * 1000);
      setRunningInterval(interval);
      return () => clearInterval(interval);
    } else if (!syncInProgress && runningInterval) {
      clearInterval(runningInterval);
    }
  }, [syncInProgress, runningInterval]);

  async function getSyncInstances() {
    const data = await environmentSyncApi.getSyncInstances();
    if (data.ok) {
      setError(undefined);
      setSyncInProgress(data.value?.currentlyRunning ?? undefined);
      setSyncOverview(data.value?.history ?? []);
      setLatestSuccesfulRun(data.value?.latestSuccesfull ?? undefined);
    } else {
      setError(data.problem);
    }
    setLoading(false);
  }

  const startSync = () => {
    async function startEnvironmentSync() {
      const data = await environmentSyncApi.startEnvironmentSync(syncAllFiles);
      if (data.ok) {
        getSyncInstances();
      } else {
        setError(data.problem);
      }
    }
    startEnvironmentSync();
  };

  const terminateSyncInstances = () => {
    async function terminateAllSyncInstances() {
      const data = await environmentSyncApi.terminateAllSyncInstances();
      if (data.ok) {
        getSyncInstances();
      } else {
        setError(data.problem);
      }
    }
    terminateAllSyncInstances();
  };

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Sync Environments
        </h1>
      </div>
      <div className="main-container-content">
        <ErrorDisplay error={error} />
        <LoadingDisplay loading={loading} />
        {!loading && (
          <>
            <div>
              <h6>Current sync state:</h6> {!syncInProgress && <>Inactive</>}
              <p>
                {syncInProgress && (
                  <>
                    {<Spinner animation="border" role="status" size="sm" />}{" "}
                    Sync in progress, started at:{" "}
                    {new Date(syncInProgress?.createdAt).toLocaleString()}
                  </>
                )}
              </p>
              <h6>Latest succesful environment sync:</h6>{" "}
              <p>
                {latestSuccesfulRun &&
                  new Date(latestSuccesfulRun?.lastUpdatedAt).toLocaleString()}
                {!latestSuccesfulRun && <>None</>}
              </p>
            </div>
            <div>
              <div>
                <input
                  type="checkbox"
                  id={"sync-all-files-checkbox"}
                  checked={syncAllFiles}
                  onChange={() => setSyncAllFiles(!syncAllFiles)}
                />
                <label
                  htmlFor={"sync-all-files-checkbox"}
                  style={{ margin: 5 }}
                >
                  Sync all files. (Not required for update testing.)
                </label>
              </div>
              <Button
                disabled={syncInProgress !== undefined}
                variant="primary"
                onClick={() => startSync()}
                style={{
                  height: "fit-content",
                  width: "fit-content",
                  marginLeft: "5px",
                }}
              >
                Start Sync
              </Button>
              <Button
                disabled={syncInProgress === undefined}
                variant="secondary"
                onClick={() => terminateSyncInstances()}
                style={{
                  height: "fit-content",
                  width: "fit-content",
                  marginLeft: "5px",
                }}
              >
                Terminate all sync operations
              </Button>
            </div>
            <h2>Sync History</h2>

            <div
              className="shadow p-3 bg-white rounded"
              style={{ height: "95%" }}
            >
              <div className="fixed-table-header" style={{ height: "100%" }}>
                <Table hover striped>
                  <thead>
                    <tr>
                      <th>
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                            maxWidth: "fit-content",
                          }}
                          className="table-header"
                        >
                          Sync ID
                        </Form.Label>
                      </th>
                      <th>
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                          }}
                          className="table-header"
                        >
                          Started at
                        </Form.Label>
                      </th>
                      <th>
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                          }}
                          className="table-header"
                        >
                          Last status update
                        </Form.Label>
                      </th>
                      <th>
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                          }}
                          className="table-header"
                        >
                          Status
                        </Form.Label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncOverview.map((instance) => (
                      <tr
                        className="table-row-scale"
                        role="button"
                        key={instance.instanceId}
                      >
                        <td style={{ fontWeight: "600" }}>
                          {instance.instanceId}
                        </td>
                        <td className="table-data-width">
                          {new Date(instance.createdAt).toLocaleString()}
                        </td>
                        <td className="table-data-width">
                          {new Date(instance.lastUpdatedAt).toLocaleString()}
                        </td>
                        <td className="table-data-width">
                          {instance.currentStatus}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default EnvironmentSyncPage;
